import React, { useCallback } from 'react';
import { activateWithCode } from '../../../api';
import { ActivationErrorType, HttpStatusCode } from '../../../constants';
import { globalMixpanelReporter, globalBigQueryReporter, globalGrafanaReporter } from '../../../events';
import { setActivationDelayCookie } from '../../../utils/setActivationDelayCookie';
import { isAxiosError } from '../../../utils/isAxiosError';

interface UseActivateWithCodeProps {
    setError: React.Dispatch<React.SetStateAction<ActivationErrorType | null>>;
    postActivationDelay: number;
    setIsCheckingCode: (isCheckingCode: boolean) => void;
    onSuccess?: () => void;
    onError?: (error: ActivationErrorType) => void;
}

export const useActivateWithCode = ({
    postActivationDelay,
    setError,
    setIsCheckingCode,
    onSuccess,
    onError,
}: UseActivateWithCodeProps) =>
    useCallback(
        async (activationCode) => {
            globalMixpanelReporter.enrichContext((context) => ({
                codeActivationAttemptsCount: (context.codeActivationAttemptsCount || 0) + 1,
            }));
            globalBigQueryReporter.enrichContext((context) => ({
                codeActivationAttemptsCount: (context.codeActivationAttemptsCount || 0) + 1,
            }));

            try {
                setError(null);
                setIsCheckingCode(true);

                globalMixpanelReporter.send('Identification - Activation - Submit');
                globalBigQueryReporter.send('identification.activation.submit');
                globalGrafanaReporter.count('identification.%{platform}.activation.submit');

                await activateWithCode(activationCode);
                setActivationDelayCookie();

                globalMixpanelReporter.send('Identification - Activation - Completed');
                globalBigQueryReporter.send('identification.activation.completed');
                globalGrafanaReporter.count('identification.%{platform}.activation.completed');

                setTimeout(() => {
                    setIsCheckingCode(false);
                    onSuccess?.();
                }, postActivationDelay);
            } catch (error) {
                let errorType: ActivationErrorType;

                const statusCode = (error as any)?.response?.status;
                if (isAxiosError(error) || statusCode) {
                    switch (statusCode) {
                        case HttpStatusCode.BAD_REQUEST:
                            errorType = ActivationErrorType.WRONG_CODE;
                            break;
                        case HttpStatusCode.FORBIDDEN:
                            errorType = ActivationErrorType.RETRY_LIMIT_REACHED;
                            break;
                        default:
                            errorType = ActivationErrorType.GENERAL;
                            break;
                    }
                } else if (error instanceof Error) {
                    if (Object.values(ActivationErrorType).includes(error.message as ActivationErrorType)) {
                        errorType = error.message as ActivationErrorType;
                    } else {
                        errorType = ActivationErrorType.GENERAL;
                    }
                } else {
                    errorType = ActivationErrorType.GENERAL;
                }

                globalMixpanelReporter.send('Identification - Activation - Failed');
                globalBigQueryReporter.send('identification.activation.failed');
                globalGrafanaReporter.count('identification.%{platform}.activation.failed');

                setError(errorType);
                setIsCheckingCode(false);
                onError?.(errorType);
            }
        },
        [onError, onSuccess, postActivationDelay, setError, setIsCheckingCode]
    );
